import { getFormattedDate } from '@frameio/utils';

const date = new Date();
const formattedDate = getFormattedDate({ date, returnDateParts: true });

export enum WatermarkModalModes {
  SELECTION = 'SELECTION',
  BUILDER = 'BUILDER',
}

export enum WatermarkBuilderModes {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

export const DATA_POINTS = {
  USERNAME: 'WatermarkDataPointUserName',
  IP_ADDRESS: 'WatermarkDataPointIpAddress',
  EMAIL: 'WatermarkDataPointEmail',
  TIMESTAMP: 'WatermarkDataPointTimestamp',
  CUSTOM_TEXT: 'WatermarkDataPointCustomText',
  USER_INPUT: 'WatermarkDataPointUserInput',
};

export const DATA_POINT_DEFAULT_VALUES = {
  [DATA_POINTS.USERNAME]: '[Viewer’s name]',
  [DATA_POINTS.EMAIL]: '[Viewer’s email address]',
  [DATA_POINTS.IP_ADDRESS]: '192.168.123.132',
  [DATA_POINTS.TIMESTAMP]: `${(date.getMonth() + 1).toString()}-${date
    .getDate()
    .toString()}-${date.getFullYear().toString()} ${
    formattedDate.formattedTime
  } UTC`,
  [DATA_POINTS.USER_INPUT]: '[Share creator custom text]',
  [DATA_POINTS.CUSTOM_TEXT]: 'New text block',
};

// the padding specification for the streaming service provides us with these
// values for defining padding on watermark text blocks. we divide the values for
// padding in half on the client side to get a more appropriate and accurate feel
// for the browser experience
export const VERTICAL_BORDER_PX = 15;
export const HORIZONTAL_BORDER_PX = 21;

export const MODAL_HEIGHT = '75vh';
export const MODAL_WIDTH = '92vw';
export const MODAL_MAX_WIDTH = '1275px';
export const SIDEPANEL_WIDTH = '385px';

export const BASE_FOR_SCALE = 888;
export const MOBILE_WIDTH = 960;
export const MOBILE_MAX_PREVIEW_HEIGHT = '275px';

export const MARQUEE_BLOCK_PADDING = 25;

// the backend enforces a 255 character limit on name, user override,
// and custom text field inputs. let's catch that on the client sid
// and inform the user.
export const WATERMARK_STRING_CHAR_LIMIT = 255;

// exclude the most common non-printable unicode characters. stream service
// will escape special characters appropriately
export const WATERMARK_STRING_ALLOWED_CHARS_REGEX =
  /^[\u{0020}-\u{007F}\u{00A1}-\u{10FFFF}]+$/u;

export const BLOCK_SIZES = {
  SMALL: 'SMALL',
  MEDIUM: 'MEDIUM',
  LARGE: 'LARGE',
  HUGE: 'HUGE',
} as const;

export const BLOCK_SIZE_MAPPINGS = {
  [BLOCK_SIZES.SMALL]: 15,
  [BLOCK_SIZES.MEDIUM]: 27,
  [BLOCK_SIZES.LARGE]: 45,
  [BLOCK_SIZES.HUGE]: 90,
};

export const BLOCK_LINE_HEIGHT_MAPPINGS = {
  [BLOCK_SIZES.SMALL]: 18,
  [BLOCK_SIZES.MEDIUM]: 33,
  [BLOCK_SIZES.LARGE]: 54,
  [BLOCK_SIZES.HUGE]: 102,
};

export const MOTION_TYPES = {
  NONE: 'none',
  LEFT_TO_RIGHT: 'left_to_right',
  RIGHT_TO_LEFT: 'right_to_left',
} as const;

// Animation duration in pixels per second
export const ANIMATION_DURATION = {
  MARQUEE: 100,
};

export const ASPECT_RATIOS = {
  WIDE: '16:9',
  STANDARD: '4:3',
  SQUARE: '1:1',
  VERTICAL: '9:16',
  CINEMATIC: '2.39:1',
} as const;

export const ASPECT_RATIOS_NUMBERS: { [key: string]: number } = {
  '16:9': 16 / 9,
  '1:1': 1,
  '4:3': 4 / 3,
  '9:16': 9 / 16,
  '1:2': 1 / 2,
  '2.39:1': 2.39,
};

export const TEXT_ALIGNMENTS = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;

export const SAFE_ZONE_PERCENTAGE = 5;

// The minimum fraction of the preview (per axis) that a block's bounding box
// must keep overlapping the frame, so a block can overflow but never be dragged
// or resized completely out of view. Scale-invariant, so it behaves the same
// regardless of preview size.
export const MIN_BLOCK_VISIBLE_RATIO = 0.15;

export const MAX_BLOCKS = 10;
export const MIN_BLOCKS = 1;

export const DEFAULT_VALUES = {
  OPACITY: 100,
  ROTATION: 0,
  WIDTH: 10,
  CUSTOM_TEXT: 'New Watermark',
  COLOR: '#FFFFFF',
  SECTION: 'MIDDLE_CENTER',
  TEXT_ALIGNMENT: 'CENTER',
} as const;

export const CHECKBOX_LIST = [
  { label: 'Name', prop: 'displayName' },
  { label: 'Date & time', prop: 'displayDateTime' },
  { label: 'Email', prop: 'displayEmail' },
  {
    label: `Share creator's custom text`,
    prop: 'displayShareCreator',
  },
  { label: 'IP Address', prop: 'displayIP' },
];

// used in place of 45 in places where 45 will cause a divide by 0 issue
export const LIM_45 = 44.99;
